import { GroupPerformerTypeFilter, GroupPerformerTypes } from '@shared/types';
import { Role, RoleGroup, RolePerformerType, RoleType } from '@talon/web/lib/api';
import { filterRoleTypes } from '@talon/web/src/utils/listFilters';
import { Box, BoxProps, Button, CheckBox, FormField, Layer, Text } from 'grommet';
import { Bike, Close, User, UserPolice } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { ProjectCostumePanel, ProjectPropPanel } from '.';
import { EditableText, Selector, TalonCard } from '..';
import { useProjectStore, useRoleStore } from '../../../context';

export type ProjectRolesPanelProps = {
  cardProps?: BoxProps;
  headerBackground?: string;
};

export const ProjectRolesPanel = observer(({ cardProps, headerBackground }: ProjectRolesPanelProps) => {
  const roleStore = useRoleStore();
  const projectStore = useProjectStore();
  const [roles, setRoles] = useState<Role[]>([]);
  const [roleGroups, setRoleGroups] = useState<RoleGroup[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [modalRole, setModalRole] = useState<Role | undefined>(undefined);

  const rolesAdded = useRef(0);

  useEffect(() => {
    const getRoles = async () => {
      if (!projectStore.currentProject?.id) return;
      const roles = await roleStore.getRolesByProjectId(projectStore.currentProject.id);
      if (roles) {
        setRoles(roles);
        if (!rolesAdded.current) rolesAdded.current = roles.length;
      }
    };
    const getRoleGroups = async () => {
      const roleGroups = await roleStore.getRoleGroupsByProjectId(projectStore.currentProject?.id || '');
      if (!roleGroups) return;
      setRoleGroups(roleGroups);
    };
    getRoles();
    getRoleGroups();
  }, [projectStore.currentProject?.id]);

  const newRole = async () => {
    if (!projectStore.currentProject?.id) return;
    const roleRequest = {
      name: `New Role ${(rolesAdded.current += 1)}`,
      type: RoleType.Actor,
      performerType: RolePerformerType.OnCameraPrincipal,
      projectId: projectStore.currentProject?.id,
    };
    const role = await roleStore.createRole(roleRequest);
    roles && role && setRoles([...roles, role]);
  };

  const deleteRoles = async () => {
    selected.forEach(async (id) => await roleStore.deleteRole(id));
    setRoles(roles.filter((r) => !selected.includes(r.id)));
    setSelected([]);
  };

  const updateRole = async (update: Role, index: number, groupId?: string) => {
    const role = await roleStore.updateRole(update, groupId);
    if (!role) return;
    const newRoles = [...roles];
    newRoles[index] = role;
    setRoles(newRoles);
  };

  const selectedNames = () => {
    const names = selected.map((s) => roles.find((item) => item.id === s)?.name);
    return names.join(', ');
  };

  return (
    <TalonCard
      newItem={newRole}
      deleteItems={deleteRoles}
      label={'Role'}
      labelPlural={'Roles'}
      addIcon={<User />}
      selectedNames={selectedNames()}
      cardProps={cardProps}
      color="accent-1"
      headerBackground={headerBackground}
    >
      {roles.map((role, i) => (
        <Box
          direction="row"
          key={role.id}
          align="center"
          gap="medium"
          pad="xsmall"
          justify="between"
          border={{ side: 'bottom', color: 'light-4' }}
        >
          <Box direction="row" align="center" gap="small">
            <CheckBox
              checked={selected.includes(role.id)}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelected([...selected, role.id]);
                } else {
                  setSelected(selected.filter((id) => id !== role.id));
                }
              }}
              pad={{ horizontal: 'small' }}
            />
            <FormField label={<Text size="xsmall">Role</Text>}>
              <EditableText val={role.name} onChange={(name) => updateRole({ ...role, name }, i)} />
            </FormField>
          </Box>

          <FormField label={<Text size="xsmall">Role Performer Type</Text>}>
            <Selector
              placeholder={'Role Performer Type'}
              options={Object.values(RolePerformerType).map((type) => ({ id: type, name: type }))}
              value={role.performer_type}
              onChange={(type) => updateRole({ ...role, performer_type: type as RolePerformerType }, i)}
              size="small"
            />
          </FormField>

          <FormField label={<Text size="xsmall">Role Type</Text>}>
            <Selector
              placeholder={'Role Type'}
              options={Object.values(RoleType)
                .filter((roleType) =>
                  role.performer_type ? filterRoleTypes(role.performer_type).includes(roleType) : true
                )
                .map((type) => ({ id: type, name: type }))}
              value={role.type}
              onChange={(type) => updateRole({ ...role, type: type as RoleType }, i)}
              size="small"
            />
          </FormField>

          {role.performer_type &&
            [...GroupPerformerTypes, ...GroupPerformerTypeFilter].includes(role.performer_type) && (
            <FormField label={<Text size="xsmall">Role Group</Text>}>
              <Selector
                placeholder={'Role Group'}
                options={roleGroups.map((group) => ({ id: group.id, name: group.name }))}
                value={role.role_group?.id}
                onChange={(group) => updateRole({ ...role }, i, group)}
                size="small"
              />
            </FormField>
          )}
          <Button
            onClick={() => {
              setModalRole(role);
            }}
            icon={
              <Box>
                <UserPolice color={role.costumes?.length ? 'accent-3' : 'light-3'} />
                <Bike color={role.props?.length ? 'accent-3' : 'light-3'} />
              </Box>
            }
            secondary
            size="small"
            margin={{ left: 'small' }}
            tip={'Manage Prop/Costume Reimbursements'}
            hoverIndicator
          />
        </Box>
      ))}
      {roles.length === 0 && <Box pad="small">No roles found</Box>}
      {modalRole && (
        <Layer onClickOutside={() => setModalRole(undefined)} onEsc={() => setModalRole(undefined)}>
          <Box direction="column" pad="small" gap="small" width="large" height="medium">
            <Box direction="row" pad="small" justify="between" align="center">
              <Text alignSelf="center">Add Prop/Costume Reimbursements for {modalRole.name}</Text>
              <Close onClick={() => setModalRole(undefined)} cursor="pointer" />
            </Box>
            <Box pad="small" direction="row" gap="small" justify="around" align="center">
              <ProjectPropPanel role={modalRole} />
              <ProjectCostumePanel role={modalRole} />
            </Box>
          </Box>
        </Layer>
      )}
    </TalonCard>
  );
});
